<template>
  <div
    class="leaderboard-card"
    :class="{
      'top-one': entry.rank === 1,
      'top-two': entry.rank === 2,
      'top-three': entry.rank === 3,
      'current-user': isCurrentUser
    }"
  >
    <!-- Header: Rank and Country -->
    <div class="card-header">
      <div class="rank-badge" :class="rankBadgeClass">
        {{ formatRank(entry.rank) }}
      </div>
      <div v-if="entry.country_codes && entry.country_codes.length > 0" class="country-flags">
        <span
          v-for="code in entry.country_codes.slice(0, 3)"
          :key="code"
          class="country-flag-mini"
          :title="code"
        >
          {{ getCountryFlag(code) }}
        </span>
        <span v-if="entry.country_codes.length > 3" class="more-countries">
          +{{ entry.country_codes.length - 3 }}
        </span>
      </div>
    </div>

    <!-- Body: User Info -->
    <div class="card-body">
      <div class="user-avatar">
        <i class="fas fa-user-circle"></i>
      </div>
      <div class="user-info">
        <div class="username">{{ entry.username }}</div>
        <div class="user-badge" v-if="isCurrentUser">
          <i class="fas fa-crown"></i>
          <span>You</span>
        </div>
      </div>
    </div>

    <!-- Footer: Score -->
    <div class="card-footer">
      <div class="score-display">
        <i class="fas fa-star score-icon"></i>
        <span class="score-value">{{ entry.total_score }}</span>
        <span class="score-label">points</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LeaderboardCard',
  props: {
    entry: {
      type: Object,
      required: true
    },
    currentUserId: {
      type: String,
      default: null
    }
  },
  computed: {
    isCurrentUser() {
      return this.currentUserId && this.entry.user_id === this.currentUserId
    },
    rankBadgeClass() {
      if (this.entry.rank === 1) return 'rank-gold'
      if (this.entry.rank === 2) return 'rank-silver'
      if (this.entry.rank === 3) return 'rank-bronze'
      return 'rank-default'
    }
  },
  methods: {
    formatRank(rank) {
      if (rank === 1) return '🥇'
      if (rank === 2) return '🥈'
      if (rank === 3) return '🥉'
      return `#${rank}`
    },
    getCountryFlag(countryCode) {
      if (!countryCode || countryCode.length !== 2) return '🌍'
      const codePoints = countryCode
        .toUpperCase()
        .split('')
        .map(char => 127397 + char.charCodeAt())
      return String.fromCodePoint(...codePoints)
    }
  }
}
</script>

<style scoped>
.leaderboard-card {
  background: white;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.leaderboard-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.12);
}

/* Top 3 styling */
.top-one {
  background: linear-gradient(135deg, #fff9e6 0%, #ffffff 100%);
  border-color: #ffd700;
}

.top-two {
  background: linear-gradient(135deg, #f5f5f5 0%, #ffffff 100%);
  border-color: #c0c0c0;
}

.top-three {
  background: linear-gradient(135deg, #fff4e6 0%, #ffffff 100%);
  border-color: #cd7f32;
}

/* Current user highlight */
.current-user {
  background: linear-gradient(135deg, #ede7f6 0%, #ffffff 100%);
  border-color: #667eea;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.2);
}

/* Card Header */
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.rank-badge {
  font-size: 1.5rem;
  font-weight: bold;
  min-width: 60px;
  text-align: center;
}

.rank-gold {
  color: #ffd700;
  text-shadow: 0 0 10px rgba(255, 215, 0, 0.5);
}

.rank-silver {
  color: #c0c0c0;
  text-shadow: 0 0 10px rgba(192, 192, 192, 0.5);
}

.rank-bronze {
  color: #cd7f32;
  text-shadow: 0 0 10px rgba(205, 127, 50, 0.5);
}

.rank-default {
  color: #6b7280;
}

.country-flags {
  display: flex;
  align-items: center;
  gap: 4px;
}

.country-flag-mini {
  font-size: 1rem;
}

.more-countries {
  font-size: 0.75rem;
  color: #6b7280;
  margin-left: 4px;
}

/* Card Body */
.card-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.user-avatar {
  font-size: 4rem;
  color: #667eea;
}

.top-one .user-avatar {
  color: #ffd700;
}

.top-two .user-avatar {
  color: #c0c0c0;
}

.top-three .user-avatar {
  color: #cd7f32;
}

.user-info {
  text-align: center;
}

.username {
  font-size: 1.125rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 4px;
}

.user-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
}

.user-badge i {
  font-size: 0.875rem;
  color: #ffd700;
}

/* Card Footer */
.card-footer {
  display: flex;
  justify-content: center;
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;
}

.score-display {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(102, 126, 234, 0.1);
  padding: 10px 20px;
  border-radius: 20px;
}

.score-icon {
  color: #fbbf24;
  font-size: 1.25rem;
}

.score-value {
  font-size: 1.5rem;
  font-weight: bold;
  color: #667eea;
}

.score-label {
  font-size: 0.875rem;
  color: #6b7280;
}

/* Animation */
@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.leaderboard-card {
  animation: slideIn 0.3s ease-out;
}
</style>
